import { useQuery, useQueryClient } from 'react-query'
import axios from 'axios'
import { useCallback, useMemo } from 'react'

const API_URL = process.env.REACT_APP_API_URL

// Query keys for consistent caching
export const GLOBAL_QUERY_KEYS = {
  CUSTOMERS: 'global-customers',
  INTERPRETERS: 'global-interpreters',
  REQUESTERS: 'global-requesters',
  LANGUAGES: 'global-languages',
  SERVICE_TYPES: 'global-service-types',
  COMMUNICATION_TYPES: 'global-communication-types',
  INVOICE_STATUS: 'global-invoice-status',
  INVOICE_TERMS: 'global-invoice-terms',
  ROLES_INTERPRETER: 'global-roles-interpreter',
  ROLES_CONSUMER: 'global-roles-consumer',
  ROLES_SYSTEM: 'global-roles-system',
  ALL_ACCOUNTS: 'global-all-accounts',
  APPLICABLE_ACCOUNTS: 'global-applicable-accounts',
  GENDERS: 'global-genders',
  ACTIVE_SERVICE_TYPES: 'global-active-service-types',
  TIME_ZONES: 'global-time-zones',
  COUNTRIES: 'global-countries',
  INDUSTRIES: 'global-industries',
  LANGUAGE_MAP_PAIRS: 'global-language-map-pairs',
} as const

export type SelectOption = {value: string; label: string}

// Cache time: 30 minutes (data doesn't change frequently)
const CACHE_TIME = 30 * 60 * 1000
// Stale time: 10 minutes (consider data fresh for 10 min)
const STALE_TIME = 10 * 60 * 1000

// Common dropdown option type
export interface DropdownOption {
  key: string
  value: string
  label?: string
  identification?: string
}

interface DropdownResponse {
  data: DropdownOption[]
}

// =============== CUSTOMERS ===============
const fetchCustomers = async (): Promise<DropdownOption[]> => {
  const response = await axios.get<DropdownResponse>(
    `${API_URL}/customer/getall-shortlist/Consumer/0`
  )
  return response.data.data || []
}

interface QueryOptions {
  enabled?: boolean
}

export const useCustomers = (options?: QueryOptions) => {
  return useQuery(GLOBAL_QUERY_KEYS.CUSTOMERS, fetchCustomers, {
    staleTime: STALE_TIME,
    cacheTime: CACHE_TIME,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    enabled: options?.enabled ?? true,
  })
}

// =============== APPLICABLE ACCOUNTS ===============
const fetchApplicableAccounts = async (): Promise<DropdownOption[]> => {
  const response = await axios.get<DropdownResponse>(
    `${API_URL}/customer/applicable-accounts/shortlist`
  )
  return response.data.data || []
}

export const useApplicableAccounts = (options?: QueryOptions) => {
  return useQuery(GLOBAL_QUERY_KEYS.APPLICABLE_ACCOUNTS, fetchApplicableAccounts, {
    staleTime: STALE_TIME,
    cacheTime: CACHE_TIME,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    enabled: options?.enabled ?? true,
  })
}

// =============== INTERPRETERS ===============
const fetchInterpreters = async (): Promise<DropdownOption[]> => {
  const response = await axios.get<DropdownResponse>(
    `${API_URL}/accounts/dd-list-accounts-filter/INTERPRETER`
  )
  return response.data.data || []
}

export const useInterpreters = () => {
  return useQuery(GLOBAL_QUERY_KEYS.INTERPRETERS, fetchInterpreters, {
    staleTime: STALE_TIME,
    cacheTime: CACHE_TIME,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  })
}

// =============== REQUESTERS ===============
const fetchRequesters = async (): Promise<DropdownOption[]> => {
  const response = await axios.get<DropdownResponse>(
    `${API_URL}/accounts/dd-list-accounts-filter/CONSUMER`
  )
  return response.data.data || []
}

const fetchAllAccountsForFilter = async (): Promise<DropdownOption[]> => {
  const userTypes = ['INTERPRETER', 'CONSUMER', 'SYSTEM']
  const responses = await Promise.all(
    userTypes.map((userType) =>
      axios.get<DropdownResponse>(`${API_URL}/accounts/dd-list-accounts-filter/${userType}`)
    )
  )
  const seen = new Set<string>()
  const merged: DropdownOption[] = []
  for (const response of responses) {
    for (const item of response.data.data || []) {
      const key = String(item.key ?? '')
      if (!key || seen.has(key)) continue
      seen.add(key)
      merged.push(item)
    }
  }
  return merged.sort((a, b) => String(a.value ?? '').localeCompare(String(b.value ?? '')))
}

export const useAllAccountsForFilter = () => {
  return useQuery(GLOBAL_QUERY_KEYS.ALL_ACCOUNTS, fetchAllAccountsForFilter, {
    staleTime: STALE_TIME,
    cacheTime: CACHE_TIME,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  })
}

export const useRequesters = (options?: QueryOptions) => {
  return useQuery(GLOBAL_QUERY_KEYS.REQUESTERS, fetchRequesters, {
    staleTime: STALE_TIME,
    cacheTime: CACHE_TIME,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    enabled: options?.enabled ?? true,
  })
}

// =============== LANGUAGES ===============
const fetchLanguages = async (): Promise<DropdownOption[]> => {
  const response = await axios.get<DropdownResponse>(
    `${API_URL}/master/languages/active-shortlist`
  )
  return response.data.data || []
}

export const useLanguages = (options?: QueryOptions) => {
  return useQuery(GLOBAL_QUERY_KEYS.LANGUAGES, fetchLanguages, {
    staleTime: STALE_TIME,
    cacheTime: CACHE_TIME,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    enabled: options?.enabled ?? true,
  })
}

// =============== SERVICE TYPES ===============
const fetchServiceTypes = async (): Promise<DropdownOption[]> => {
  const response = await axios.get<DropdownResponse>(
    `${API_URL}/master/getall/SERVICE_TYPE`
  )
  return response.data.data || []
}

export const useServiceTypes = (options?: QueryOptions) => {
  return useQuery(GLOBAL_QUERY_KEYS.SERVICE_TYPES, fetchServiceTypes, {
    staleTime: STALE_TIME,
    cacheTime: CACHE_TIME,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    enabled: options?.enabled ?? true,
  })
}

const fetchActiveMasterShortlist = async (code: string): Promise<DropdownOption[]> => {
  const response = await axios.get<DropdownResponse>(
    `${API_URL}/master/data/${code}/active-shortlist`
  )
  return response.data.data || []
}

export const useGenders = (options?: QueryOptions) => {
  return useQuery(GLOBAL_QUERY_KEYS.GENDERS, () => fetchActiveMasterShortlist('GENDER'), {
    staleTime: STALE_TIME,
    cacheTime: CACHE_TIME,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    enabled: options?.enabled ?? true,
  })
}

export const useActiveServiceTypes = (options?: QueryOptions) => {
  return useQuery(
    GLOBAL_QUERY_KEYS.ACTIVE_SERVICE_TYPES,
    () => fetchActiveMasterShortlist('SERVICE_TYPE'),
    {
      staleTime: STALE_TIME,
      cacheTime: CACHE_TIME,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      enabled: options?.enabled ?? true,
    }
  )
}

interface TimeZoneOption {
  code: string
  name: string
}

const fetchTimeZones = async (): Promise<TimeZoneOption[]> => {
  const response = await axios.get<{data: TimeZoneOption[]}>(
    `${API_URL}/master/time-zone/active-shortlist`
  )
  return response.data.data || []
}

export const useTimeZones = (options?: QueryOptions) => {
  return useQuery(GLOBAL_QUERY_KEYS.TIME_ZONES, fetchTimeZones, {
    staleTime: STALE_TIME,
    cacheTime: CACHE_TIME,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    enabled: options?.enabled ?? true,
  })
}

const fetchCountries = async (): Promise<DropdownOption[]> => {
  const response = await axios.get<DropdownResponse>(`${API_URL}/country/getall`)
  return response.data.data || []
}

export const useCountries = (options?: QueryOptions) => {
  return useQuery(GLOBAL_QUERY_KEYS.COUNTRIES, fetchCountries, {
    staleTime: STALE_TIME,
    cacheTime: CACHE_TIME,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    enabled: options?.enabled ?? true,
  })
}

// =============== COMMUNICATION TYPES ===============
const fetchCommunicationTypes = async (): Promise<DropdownOption[]> => {
  const response = await axios.get<DropdownResponse>(
    `${API_URL}/master/getall/COMMUNICATION_TYPE`
  )
  return response.data.data || []
}

export const useCommunicationTypes = (options?: QueryOptions) => {
  return useQuery(GLOBAL_QUERY_KEYS.COMMUNICATION_TYPES, fetchCommunicationTypes, {
    staleTime: STALE_TIME,
    cacheTime: CACHE_TIME,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    enabled: options?.enabled ?? true,
  })
}

export const useIndustries = (options?: QueryOptions) => {
  return useQuery(GLOBAL_QUERY_KEYS.INDUSTRIES, () => fetchActiveMasterShortlist('INDUSTRY'), {
    staleTime: STALE_TIME,
    cacheTime: CACHE_TIME,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    enabled: options?.enabled ?? true,
  })
}

/** Language from-to pairs for interpreter language map (not the same as useLanguages native shortlist). */
const fetchLanguageMapPairs = async (): Promise<SelectOption[]> => {
  const response = await axios.get<{
    data?: {languages?: Array<{value?: string; label?: string}>}
  }>(`${API_URL}/master/user-additional`)
  const languages = response.data?.data?.languages ?? []
  return languages.map((item) => ({
    value: String(item.value ?? ''),
    label: String(item.label ?? ''),
  }))
}

export const useLanguageMapPairs = (options?: QueryOptions) => {
  return useQuery(GLOBAL_QUERY_KEYS.LANGUAGE_MAP_PAIRS, fetchLanguageMapPairs, {
    staleTime: STALE_TIME,
    cacheTime: CACHE_TIME,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    enabled: options?.enabled ?? true,
  })
}

export const dropdownToSelectOptions = (items?: DropdownOption[]): SelectOption[] =>
  (items ?? []).map((d) => ({
    value: String(d.key ?? ''),
    label: String(d.value ?? ''),
  }))

// =============== INVOICE STATUS ===============
const fetchInvoiceStatus = async (): Promise<DropdownOption[]> => {
  const response = await axios.get<DropdownResponse>(
    `${API_URL}/invoice/ddlist-invoice-status`
  )
  return response.data.data || []
}

export const useInvoiceStatus = () => {
  return useQuery(GLOBAL_QUERY_KEYS.INVOICE_STATUS, fetchInvoiceStatus, {
    staleTime: STALE_TIME,
    cacheTime: CACHE_TIME,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  })
}

// =============== INVOICE TERMS ===============
const fetchInvoiceTerms = async (): Promise<DropdownOption[]> => {
  const response = await axios.get<DropdownResponse>(
    `${API_URL}/invoice/ddlist-terms`
  )
  return response.data.data || []
}

export const useInvoiceTerms = () => {
  return useQuery(GLOBAL_QUERY_KEYS.INVOICE_TERMS, fetchInvoiceTerms, {
    staleTime: STALE_TIME,
    cacheTime: CACHE_TIME,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  })
}

// =============== ROLES ===============
const normalizeRoleOptions = (items: unknown): DropdownOption[] => {
  if (!Array.isArray(items)) return []

  return items.map((item: any) => ({
    key: String(item.key ?? item.id ?? ''),
    value: String(item.value ?? item.name ?? ''),
  }))
}

const fetchRoles = async (userType: string): Promise<DropdownOption[]> => {
  const response = await axios.get(`${API_URL}/roles/ddlist-role/${userType}`)
  const payload = response.data

  // ddlist-role returns a bare array; getall-shortlist returns { data: [...] }
  if (Array.isArray(payload)) {
    return normalizeRoleOptions(payload)
  }

  if (Array.isArray(payload?.data)) {
    return normalizeRoleOptions(payload.data)
  }

  const shortlistResponse = await axios.get(`${API_URL}/roles/getall-shortlist/${userType}`)
  return normalizeRoleOptions(shortlistResponse.data?.data)
}

export const useRoles = (userType: 'INTERPRETER' | 'CONSUMER' | 'SYSTEM') => {
  const queryKey = userType === 'INTERPRETER' 
    ? GLOBAL_QUERY_KEYS.ROLES_INTERPRETER 
    : userType === 'CONSUMER' 
      ? GLOBAL_QUERY_KEYS.ROLES_CONSUMER 
      : GLOBAL_QUERY_KEYS.ROLES_SYSTEM

  return useQuery([queryKey, userType], () => fetchRoles(userType), {
    staleTime: STALE_TIME,
    cacheTime: CACHE_TIME,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  })
}

// =============== DROPDOWN OPTIONS TRANSFORMER ===============
export const useDropdownOptions = <T extends DropdownOption>(
  data: T[] | undefined,
  options?: {
    filterFn?: (item: T) => boolean
    mapFn?: (item: T) => { value: string; label: string; [key: string]: any }
  }
) => {
  return useMemo(() => {
    if (!data) return []
    
    let filtered = data
    if (options?.filterFn) {
      filtered = data.filter(options.filterFn)
    }

    if (options?.mapFn) {
      return filtered.map(options.mapFn)
    }

    return filtered.map((item) => ({
      value: item.key ?? '',
      label: item.value ?? '',
    }))
  }, [data, options?.filterFn, options?.mapFn])
}

// =============== PREFETCH GLOBAL DATA ===============
export const usePrefetchGlobalData = () => {
  const queryClient = useQueryClient()

  const prefetchAll = useCallback(async () => {
    // Prefetch all commonly used data in parallel
    await Promise.allSettled([
      queryClient.prefetchQuery(GLOBAL_QUERY_KEYS.CUSTOMERS, fetchCustomers, {
        staleTime: STALE_TIME,
      }),
      queryClient.prefetchQuery(GLOBAL_QUERY_KEYS.INTERPRETERS, fetchInterpreters, {
        staleTime: STALE_TIME,
      }),
      queryClient.prefetchQuery(GLOBAL_QUERY_KEYS.LANGUAGES, fetchLanguages, {
        staleTime: STALE_TIME,
      }),
      queryClient.prefetchQuery(GLOBAL_QUERY_KEYS.SERVICE_TYPES, fetchServiceTypes, {
        staleTime: STALE_TIME,
      }),
      queryClient.prefetchQuery(GLOBAL_QUERY_KEYS.COMMUNICATION_TYPES, fetchCommunicationTypes, {
        staleTime: STALE_TIME,
      }),
    ])
  }, [queryClient])

  return { prefetchAll }
}

// =============== INVALIDATE GLOBAL DATA ===============
export const useInvalidateGlobalData = () => {
  const queryClient = useQueryClient()

  const invalidateCustomers = useCallback(() => {
    queryClient.invalidateQueries(GLOBAL_QUERY_KEYS.CUSTOMERS)
  }, [queryClient])

  const invalidateInterpreters = useCallback(() => {
    queryClient.invalidateQueries(GLOBAL_QUERY_KEYS.INTERPRETERS)
  }, [queryClient])

  const invalidateLanguages = useCallback(() => {
    queryClient.invalidateQueries(GLOBAL_QUERY_KEYS.LANGUAGES)
  }, [queryClient])

  const invalidateAll = useCallback(() => {
    Object.values(GLOBAL_QUERY_KEYS).forEach((key) => {
      queryClient.invalidateQueries(key)
    })
  }, [queryClient])

  return {
    invalidateCustomers,
    invalidateInterpreters,
    invalidateLanguages,
    invalidateAll,
  }
}
